import { useAuthenticationState } from './internal/AuthenticationContext';
import { type UserSession } from './types';

const EN = {
  loading: 'Loading...',
  loggingOut: 'Logging out',
  sessionOutOfDateLogout: 'This session is out of date. Logging out...',
  outage: {
    badge: 'Temporary Outage',
    headline: 'We are currently seeing errors with our Authentication partner platform.',
    impact:
      'This may impact your ability to log in to our web and mobile platforms. We are investigating with our partner and will continue to provide updates as they become available.',
    unaffected: 'Video loading and video recordings are not affected at this time.',
    supportIntro: 'As always, you can reach out to our Customer Support team by email at',
    supportEmail: 'support@solinkcorp.com',
    supportOr: 'or by phone at',
    supportPhone: '1-888-335-9844',
    statusIntro: 'For up to date information, you can view our status page',
    apology:
      'We apologize for any inconvenience this may cause. Our team is working hard to resolve the issue, and we will keep you updated with the latest information.',
    signOff: 'Thank you,',
    team: 'The Solink Team',
    latestUpdate: 'Latest update',
    lastChecked: 'Last checked',
    checkAgain: 'Check again',
    statusUnreachable: "Couldn't reach the status page",
    restored: 'Service has been restored.',
    restoredHint: 'You can reload the page to continue.',
    reloadingIn: 'Reloading in',
    reloadNow: 'Reload now',
  },
  unexpectedError:
    'An unexpected error occurred. Please reload or retry the operation in a few minutes. If problem persists, contact Solink support.',
  wrongCustomer: 'You are logged in as the wrong customer to access this page.  Logging out...',
};

export type Translations = typeof EN;
export type OutageTranslations = Translations['outage'];

const ES: Translations = {
  loading: 'Cargando...',
  loggingOut: 'Saliendo de tu cuenta',
  sessionOutOfDateLogout: 'Esta sesión está desactualizada. Saliendo de tu cuenta...',
  // ES currently mirrors English body copy for the outage page.
  outage: EN.outage,
  unexpectedError:
    'Se ha producido un error inesperado. Vuelva a intentar realizar la operación en unos minutos. Si el problema continúa, póngase en contacto con el soporte técnico de Solink.',
  wrongCustomer:
    'Ha iniciado sesión como el cliente equivocado para acceder a esta página. Cerrando sesión...',
};

const FR: Translations = {
  loading: 'Chargement...',
  loggingOut: 'Déconnecter',
  sessionOutOfDateLogout: 'Cette session est obsolète. Déconnecter...',
  outage: {
    badge: 'Temporary Outage',
    headline:
      "Nous constatons actuellement des erreurs avec notre plateforme de partenaire d'authentification.",
    impact:
      "Cela peut avoir un impact sur votre capacité à vous connecter à nos plateformes Web et mobile. Nous enquêtons avec notre partenaire et continuerons de fournir des mises à jour dès qu'elles seront disponibles.",
    unaffected:
      'Le chargement vidéo et les enregistrements vidéo ne sont pas affectés pour le moment.',
    supportIntro:
      'Comme toujours, vous pouvez contacter notre équipe de soutien à la clientèle par courriel à',
    supportEmail: 'support@solinkcorp.com',
    supportOr: 'ou par téléphone au',
    supportPhone: '1-888-335-9844',
    statusIntro: "Pour des informations à jour, vous pouvez consulter notre page d'état",
    apology:
      'Nous nous excusons pour les désagréments causés. Notre équipe travaille fort pour résoudre le problème et nous vous tiendrons au courant des dernières informations.',
    signOff: 'Merci,',
    team: "L'équipe Solink",
    latestUpdate: 'Dernière mise à jour',
    lastChecked: 'Dernière vérification',
    checkAgain: 'Vérifier à nouveau',
    statusUnreachable: "Impossible d'atteindre la page d'état",
    restored: 'Le service a été rétabli.',
    restoredHint: 'Vous pouvez recharger la page pour continuer.',
    reloadingIn: 'Rechargement dans',
    reloadNow: 'Recharger maintenant',
  },
  unexpectedError:
    "Une erreur inattendue s’est produite. Retentez l'opération dans quelques minutes. Si le problème persiste, contactez le support technique Solink.",
  wrongCustomer:
    'Vous êtes connecté en tant que mauvais client pour accéder à cette page. Déconnexion...',
};

const TRANSLATIONS = {
  en: EN,
  fr: FR,
  es: ES,
};

export function getCoreTranslations(userSession: UserSession | undefined): Translations {
  const locale = userSession?.profile.preferences?.locale || 'en-US';
  const language = locale.split('-')[0] as keyof typeof TRANSLATIONS;
  return TRANSLATIONS[language] ?? TRANSLATIONS.en;
}

/**
 * Returns the set of "core" translations.  This is the minimal translations we
 * need for the login screen and other core functionality.  These components
 * can't use the regular `useLocale()` both because we don't want to load all
 * translations at startup, and because there's no redux loaded yet at this point.
 */
export function useCoreTranslations(): Translations {
  const [authState] = useAuthenticationState();
  return getCoreTranslations(authState.userSession);
}
